import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FranchiseIntranetUpgradeService } from '../franchise-intranet-upgrade.service';
import { FranchiseIntranetUpgradeModel } from '../franchise-intranet-upgrade.model';
import { AppTextService } from '@fi-shared/app-text';
import { DropdownSettingsModelV2 } from '@fi-shared/dropdown';
import {
    FiUpgradeStatusType,
    IUpgradesStatusType,
    IUpgradeStatusData,
    UpgradeStatusTypes,
} from '../upgrade-status-type';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { ModuleType } from '@fi-shared/types/module-type';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { UpcomingUpgradeStatus } from '../franchise-intranet-upgrade-upcoming-status';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiAppSettings } from '@fi-shared/fi-app-settings';
import { EditTabModel, EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { isDefined } from '@fi-shared/utils';

interface IUpgradedDateData {
    date: Date;
    time: Date;
    minDateTime: Date;
}

@Component({
    selector: 'fi-franchise-intranet-upgrade-edit',
    templateUrl: './franchise-intranet-upgrade-edit.component.html',
    styleUrls: ['./franchise-intranet-upgrade-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-franchise-intranet-upgrade-edit' },
    standalone: false,
})
export class FiFranchiseIntranetUpgradeEditComponent implements OnInit {
    @Input('franchiseIntranetUpgrade') FranchiseIntranetUpgrade: FranchiseIntranetUpgradeModel;
    @Input() upcomingUpgradeStatus: UpcomingUpgradeStatus;

    upgradeStatusTypesListData = new DropdownSettingsModelV2<INameIdObj>({
        filterBy: 'Name',
    });
    UpgradedOnDate: IUpgradedDateData = { date: null, time: null, minDateTime: null };
    UpgradedEndDate: IUpgradedDateData = { date: null, time: null, minDateTime: null };
    upgradeTypeDropdownSettings = new DropdownSettingsModelV2<INameIdObj>({
        filterBy: 'Name',
    });
    editTabs: EditTabsModel;

    constructor(
        private franchiseIntranetUpgradeService: FranchiseIntranetUpgradeService,
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private loadingService: LoadingService,
        private saveChangesWarningService: SaveChangesWarningService,
        private stateService: FiStateService,
        private statisticService: StatisticService,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    ngOnInit() {
        if (!isDefined(this.FranchiseIntranetUpgrade)) {
            this.FranchiseIntranetUpgrade = new FranchiseIntranetUpgradeModel(this.fiFormatDateService);
        }
        this.setInitialData();
        this.setHeaderData();
    }

    private setInitialData() {
        const upgradeStatusData: IUpgradeStatusData[] = UpgradeStatusTypes.map(
            (upgradeStatusType: IUpgradesStatusType) => {
                return {
                    Id: upgradeStatusType.Id,
                    Name: this.appTextService.get(upgradeStatusType.TextId),
                };
            },
        ).sort((a: IUpgradeStatusData, b: IUpgradeStatusData) => {
            if (a.Name < b.Name) return -1;
            if (a.Name > b.Name) return 1;
            return 0;
        });

        this.upgradeStatusTypesListData.setDataSet(upgradeStatusData);

        const upgradedOn = new Date(this.FranchiseIntranetUpgrade.UpgradeOn);
        this.UpgradedOnDate.date = new Date(upgradedOn.getTime());
        this.UpgradedOnDate.time = new Date(upgradedOn.getTime());

        if (this.FranchiseIntranetUpgrade.Id && upgradedOn.getTime() < Date.now()) {
            this.UpgradedOnDate.minDateTime = new Date(upgradedOn.getTime());
        } else {
            this.UpgradedOnDate.minDateTime = new Date(Date.now() - FiAppSettings.CountDownTime); // set min dateTime as 2 hrs before upgradedOn time
        }

        const upgradedEnd = this.getUpgradeEndTime();
        this.UpgradedEndDate.date = new Date(upgradedEnd);
        this.UpgradedEndDate.time = new Date(upgradedEnd);
        this.UpgradedEndDate.minDateTime = new Date();

        this.updateEndDateMin();

        this.upgradeTypeDropdownSettings.setDataSet(this.getUpgradeTypeDataSet());

        const availableStatuses = this.upgradeTypeDropdownSettings.dataSet.map((item) => item.Id);
        if (availableStatuses.indexOf(this.FranchiseIntranetUpgrade.UpgradeVersionType) === -1)
            this.FranchiseIntranetUpgrade.UpgradeVersionType = this.upgradeTypeDropdownSettings.dataSet[0].Id;

        const getModelData = () => {
            return {
                upgrade: this.FranchiseIntranetUpgrade,
                date: this.UpgradedOnDate,
            };
        };

        this.statisticService.Visit(
            ModuleType.CustomFranchiseIntranetUpgrade,
            ObjectTypes.CustomFranchiseIntranetUpgrade,
            this.stateService.params.Id,
        );

        this.upgradedOnDateChange();
        this.upgradedOnTimeChange();
        this.upgradedEndDateChange();
        this.upgradedEndTimeChange();

        this.saveChangesWarningService.start(getModelData);
    }

    getUpgradeTypeDataSet(): INameIdObj[] {
        const upgradeTypeDataSet: INameIdObj[] = [];

        if (
            this.FranchiseIntranetUpgrade.Id ||
            (this.upcomingUpgradeStatus !== UpcomingUpgradeStatus.Major &&
                this.upcomingUpgradeStatus !== UpcomingUpgradeStatus.Both)
        ) {
            upgradeTypeDataSet.push({
                Id: this.franchiseIntranetUpgradeService.UpgradeVersionType.MajorUpgrade.Id,
                Name: this.appTextService.get(
                    this.franchiseIntranetUpgradeService.UpgradeVersionType.MajorUpgrade.TextId,
                ),
            });
        }

        if (
            this.FranchiseIntranetUpgrade.Id ||
            (this.upcomingUpgradeStatus !== UpcomingUpgradeStatus.Minor &&
                this.upcomingUpgradeStatus !== UpcomingUpgradeStatus.Both)
        ) {
            upgradeTypeDataSet.push({
                Id: this.franchiseIntranetUpgradeService.UpgradeVersionType.MinorUpgrade.Id,
                Name: this.appTextService.get(
                    this.franchiseIntranetUpgradeService.UpgradeVersionType.MinorUpgrade.TextId,
                ),
            });
        }

        return upgradeTypeDataSet;
    }

    isUpgradeTypeSelectDisabled(): boolean {
        return !!this.FranchiseIntranetUpgrade.Id;
    }

    private setHeaderData() {
        this.editTabs = new EditTabsModel(this.getPageTitle());
        this.editTabs.Tabs.push(
            new EditTabModel(this.appTextService.get('common.general'), this.appTextService.get('common.general')),
        );
    }

    private getUpgradeEndTime(): Date {
        if (this.FranchiseIntranetUpgrade.UpgradeEndTime) return new Date(this.FranchiseIntranetUpgrade.UpgradeEndTime);

        const result = new Date(this.FranchiseIntranetUpgrade.UpgradeOn);
        result.setTime(result.getTime() + 10 * 60 * 1000);
        return result;
    }

    private getPageTitle(): string {
        return this.FranchiseIntranetUpgrade.Id > 0
            ? this.appTextService.get('upgrade.edit_upgrade')
            : this.appTextService.get('upgrade.new_upgrade');
    }

    mergeDateTime(date: Date, time: Date): Date {
        if (!date || !time) return null;

        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.getHours(), time.getMinutes(), 0);
    }

    private validateUpgradeDate(): boolean {
        if (this.FranchiseIntranetUpgrade.UpgradedOn < this.UpgradedOnDate.minDateTime) {
            this.fiDialogsService.error({
                messageText: 'Invalid upgrade date selected',
            });
            return false;
        }

        return true;
    }

    private updateEndDateMin() {
        const upgradeOn = this.mergeDateTime(this.UpgradedOnDate.date, this.UpgradedOnDate.time);
        const upgradeEnd = this.mergeDateTime(this.UpgradedEndDate.date, this.UpgradedEndDate.time);
        if (upgradeOn > upgradeEnd) {
            this.UpgradedEndDate.time = new Date(this.UpgradedOnDate.time.getTime() + 10 * 60 * 1000);
            this.UpgradedEndDate.date = new Date(this.UpgradedEndDate.time.getTime());
        }
        this.UpgradedEndDate.minDateTime = new Date(
            this.mergeDateTime(this.UpgradedOnDate.date, this.UpgradedOnDate.time).getTime() + 2 * 60 * 1000,
        );
    }

    save() {
        this.FranchiseIntranetUpgrade.UpgradedOn = this.mergeDateTime(
            this.UpgradedOnDate.date,
            this.UpgradedOnDate.time,
        );

        if (!this.validateUpgradeDate()) return;

        if (!this.FranchiseIntranetUpgrade.Id)
            this.FranchiseIntranetUpgrade.UpgradeStatusType = FiUpgradeStatusType.Upcoming;

        this.loadingService.trackPromise(
            this.franchiseIntranetUpgradeService
                .SaveFranchiseIntranetUpgrade(this.FranchiseIntranetUpgrade)
                .then((response) => {
                    this.saveChangesWarningService.stop();
                    this.stateService.go(FiStateNames.Upgrade.List);
                }),
        );
    }

    upgradedOnDateChange() {
        if (!this.UpgradedOnDate.date || !this.UpgradedOnDate.time) return;

        this.UpgradedOnDate.time = this.mergeDateTime(this.UpgradedOnDate.date, this.UpgradedOnDate.time);
        this.updateEndDateMin();
        this.updateUpgradedOnDate();
        this.updateUpgradeEndTime();
    }

    upgradedOnTimeChange() {
        if (!this.UpgradedOnDate.date || !this.UpgradedOnDate.time) return;

        this.UpgradedOnDate.time = this.mergeDateTime(this.UpgradedOnDate.date, this.UpgradedOnDate.time);
        this.updateEndDateMin();
        this.updateUpgradedOnDate();
        this.updateUpgradeEndTime();
    }

    private updateUpgradedOnDate() {
        this.FranchiseIntranetUpgrade.UpgradeOn = this.mergeDateTime(this.UpgradedOnDate.date, this.UpgradedOnDate.time)
            .toISOString()
            .replace('Z', '+00:00');
    }

    upgradedEndDateChange() {
        if (!this.UpgradedEndDate.date || !this.UpgradedEndDate.time) return;

        this.UpgradedEndDate.time = this.mergeDateTime(this.UpgradedEndDate.date, this.UpgradedEndDate.time);
        this.updateEndDateMin();
        this.updateUpgradeEndTime();
    }

    upgradedEndTimeChange() {
        if (!this.UpgradedEndDate.date || !this.UpgradedEndDate.time) return;

        this.UpgradedEndDate.time = this.mergeDateTime(this.UpgradedEndDate.date, this.UpgradedEndDate.time);
        this.updateEndDateMin();
        this.updateUpgradeEndTime();
    }

    private updateUpgradeEndTime() {
        this.FranchiseIntranetUpgrade.UpgradeEndTime = this.mergeDateTime(
            this.UpgradedEndDate.date,
            this.UpgradedEndDate.time,
        )
            .toISOString()
            .replace('Z', '+00:00');
    }
}
